import React, {useEffect, useMemo, useState} from 'react'
import {Button, CircularProgress, Container, TextField, Typography} from '@mui/material'
import {Box} from '@mui/system'
import {ModalPage} from 'nav/ModalPage'
import {useDispatch, useSelector} from 'react-redux'
import _ from 'lodash'
import {RootState} from '../../redux/rootReducer'
import {actions as deviceActions} from '../../redux/modules/devices'
import Device from 'models/Device'
import styles from './styles'
import AlertMessage from '../../components/AlertMessage'

const CreateDevicePage: ModalPage<{
  patientId?: string
  device?: Device
}> = props => {
  const dispatch = useDispatch()
  const loading = useSelector(({devices}: RootState) => devices.loading)
  const deviceError = useSelector(({devices}: RootState) => devices.error)
  const deviceWarning = useSelector(({devices}: RootState) => devices.warning)
  const {onClickCancel, patientId, device} = props
  const [shouldClose, setShouldClose] = useState(false)
  const [name, setName] = useState(device?.name ?? '')
  const [udid, setUdid] = useState(device?.udid ?? '')
  const [formError, setFormError] = useState<string | undefined>()

  useEffect(() => {
    dispatch(deviceActions.setError({error: undefined}))
    dispatch(deviceActions.setWarning({warning: undefined}))
  }, [])

  useEffect(() => {
    if (!patientId) {
      dispatch(deviceActions.setError({error: undefined}))
      return
    }
    if (device) {
      dispatch(deviceActions.setError({error: undefined}))
      return
    }
    dispatch(
      deviceActions.getNameByPatientId({
        patientId,
        onFound: fetchedName => setName(fetchedName),
      }),
    )
  }, [dispatch, patientId, device])

  if (loading && !shouldClose) {
    setShouldClose(true)
  }
  if (shouldClose && !deviceError && !loading) {
    onClickCancel()
  }

  const closePage = () => {
    dispatch(deviceActions.setError({error: undefined}))
    onClickCancel()
  }

  const handleNameBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = _.trim(event.currentTarget.value)
    if (_.isEmpty(value)) {
      return
    }
    if (!patientId) {
      return
    }
    dispatch(deviceActions.checkNameAvailability({name: value, patientId: patientId}))
  }

  const saveDevice = () => {
    setFormError(undefined)
    if (_.isEmpty(name) || _.isEmpty(udid)) {
      setFormError('All fields are required.')
      return
    }
    if (patientId) {
      const newDevice = new Device({name, udid, createdAt: new Date()})
      dispatch(deviceActions.add({device: newDevice, patientId}))
    } else if (device) {
      device.name = name
      device.udid = udid
      dispatch(deviceActions.update({device}))
    }
  }

  const errorMessage = useMemo(() => {
    if (formError) {
      return formError
    }

    if (deviceError) {
      return deviceError
    }

    return undefined
  }, [formError, deviceError])

  return (
    <Container>
      <Box sx={styles.modal}>
        <Typography sx={styles.headerLabel} variant="h4">
          Add device
        </Typography>

        <AlertMessage error={formError ?? deviceError} severity="error" sx={{marginBottom: 2}} />

        <AlertMessage error={deviceWarning} severity="warning" sx={{marginBottom: 2}} />

        <TextField
          sx={styles.textField}
          fullWidth
          value={name}
          onChange={e => setName(e.currentTarget.value)}
          onBlur={handleNameBlur}
          placeholder="Device name"
        />
        <TextField
          sx={styles.textField}
          fullWidth
          value={udid}
          onChange={e => setUdid(e.currentTarget.value)}
          placeholder="UDID"
        />
        <Box sx={styles.buttonContainer}>
          <Button sx={styles.cancelButton} color="warning" onClick={closePage}>
            Cancel
          </Button>
          {loading && (
            <Box sx={styles.progress}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!loading && (
            <Button onClick={saveDevice} variant="contained">
              Save
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  )
}

export default CreateDevicePage
